import React, { useState, useEffect, useRef } from 'react';
import { Pencil, CheckCircle, Target, Maximize2, Minimize2 } from 'lucide-react';

const ObjectifEditor = ({
  objectif,
  objectifType,
  onSave,
  isReadOnly = false,
  lastUpdate = null
}) => {
  const [editing, setEditing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [editedObjectif, setEditedObjectif] = useState(objectif);
  const [showUpdateAnimation, setShowUpdateAnimation] = useState(false);
  const textareaRef = useRef(null);
  
  // Type d'objectif en français pour l'affichage
  const objectifTypeInFrench = 
    objectifType === 'project' ? 'projet' : 
    objectifType === 'idea' ? 'idée' : 'désir/aspiration';
  
  // Effet pour détecter les mises à jour automatiques
  useEffect(() => {
    if (objectif !== editedObjectif && !editing) {
      setEditedObjectif(objectif);
      
      // Animation pour indiquer la mise à jour
      setShowUpdateAnimation(true);
      setTimeout(() => setShowUpdateAnimation(false), 2000);
    }
  }, [objectif]);
  
  // Passer en mode édition
  const handleEdit = () => {
    setEditing(true);
    // Focus sur le textarea après rendu
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.selectionStart = textareaRef.current.value.length;
      }
    }, 10);
  };
  
  // Sauvegarder les modifications
  const handleSave = () => {
    if (editedObjectif.trim() !== '') {
      onSave(editedObjectif.trim());
      setEditing(false);
    }
  };
  
  // Annuler l'édition
  const handleCancel = () => {
    setEditedObjectif(objectif);
    setEditing(false);
  };
  
  // Gérer les touches au clavier
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };
  
  // Basculer entre vue normale et vue étendue
  const toggleExpanded = () => {
    setExpanded(!expanded);
  };
  
  return (
    <div className={`mb-6 rounded-xl border-2 ${
      showUpdateAnimation 
        ? 'border-purple-500 bg-purple-50 transition-all duration-700' 
        : editing 
          ? 'border-blue-300 bg-blue-50' 
          : 'border-gray-200 bg-white'
    }`}>
      {/* En-tête avec titre et boutons */}
      <div className="px-5 py-3 border-b border-gray-200 flex justify-between items-center">
        <div className="flex items-center">
          <Target className="h-5 w-5 mr-2 text-purple-600" />
          <h2 className="text-lg font-semibold text-gray-800">
            Votre {objectifTypeInFrench}
          </h2>
        </div>
        <div className="flex space-x-2">
          {!isReadOnly && !editing && (
            <button
              onClick={handleEdit}
              className="p-1.5 rounded-md hover:bg-gray-100 text-gray-600 hover:text-purple-600"
              aria-label="Modifier l'objectif"
            >
              <Pencil className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={toggleExpanded}
            className="p-1.5 rounded-md hover:bg-gray-100 text-gray-600 hover:text-purple-600"
            aria-label={expanded ? "Réduire" : "Agrandir"}
          >
            {expanded ? (
              <Minimize2 className="h-4 w-4" />
            ) : (
              <Maximize2 className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>
      
      {/* Contenu de l'objectif */}
      <div className={`px-5 py-4 ${expanded ? 'max-h-80' : 'max-h-36'} overflow-y-auto transition-all duration-300`}>
        {editing ? (
          <div className="flex flex-col">
            <textarea
              ref={textareaRef}
              value={editedObjectif}
              onChange={(e) => setEditedObjectif(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full p-3 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent resize-none text-base md:text-lg leading-relaxed"
              rows={expanded ? 6 : 3}
              placeholder={`Décrivez votre ${objectifTypeInFrench}...`}
            />
            <div className="flex justify-end space-x-3 mt-3">
              <button
                onClick={handleCancel}
                className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300"
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                disabled={!editedObjectif.trim()}
                className={`px-4 py-2 rounded-lg flex items-center ${
                  !editedObjectif.trim() 
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                    : 'bg-purple-600 text-white hover:bg-purple-700'
                }`}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Valider
              </button>
            </div>
          </div>
        ) : (
          <p className="text-lg leading-relaxed">
            {editedObjectif}
          </p>
        )}
      </div>
      
      {/* Indication de mise à jour automatique */}
      {lastUpdate && !editing && (
        <div className="px-5 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
          Dernière mise à jour: {lastUpdate}
        </div>
      )}
    </div>
  );
};

export default ObjectifEditor;